import { useEffect, useState } from 'react';
import {
  Panel, Div, Card, Headline, Text, Button, Input, FormItem,
  Select, Spinner,
} from '@vkontakte/vkui';
import { useAuthStore } from '../../stores/auth';
import { useWeddingStore } from '../../stores/wedding';
import { api } from '../../api/client';
import ShareInviteButton from '../../ui/ShareInviteButton';
import { buildInviteLink, readInviteCodeFromClipboard } from '../../utils/joinCode';

type Choice = 'create' | 'join' | null;

interface OnboardingState {
  step: number;
  name: string;
  choice: Choice;
  weddingName: string;
  weddingDate: string;
  budget: string;
  currency: string;
  partnerName: string;
  joinCode: string;
}

const STORAGE_KEY = 'colibri_onboarding';

const defaultState: OnboardingState = {
  step: 0,
  name: '',
  choice: null,
  weddingName: '',
  weddingDate: '',
  budget: '',
  currency: 'RUB',
  partnerName: '',
  joinCode: '',
};

function loadState(): OnboardingState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...defaultState };
    return { ...defaultState, ...JSON.parse(raw) };
  } catch {
    return { ...defaultState };
  }
}

function saveState(s: OnboardingState) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
}

export function isOnboardingDone(): boolean {
  return localStorage.getItem(STORAGE_KEY + '_done') === '1';
}

export function markOnboardingDone() {
  localStorage.setItem(STORAGE_KEY + '_done', '1');
}

export function resetOnboarding() {
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(STORAGE_KEY + '_done');
}

export default function OnboardingWizard({ onDone }: { onDone: () => void }) {
  const { user, loadUser } = useAuthStore();
  const { createWedding, joinWedding, loadWeddings, currentWedding } = useWeddingStore();
  const [s, setS] = useState<OnboardingState>(() => {
    const st = loadState();
    if (!st.name && user?.first_name) st.name = user.first_name;
    return st;
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    saveState(s);
  }, [s]);

  function patch(p: Partial<OnboardingState>) {
    setS((prev) => ({ ...prev, ...p }));
  }

  function finish() {
    localStorage.setItem(STORAGE_KEY + '_done', '1');
    onDone();
  }

  async function saveName() {
    try {
      await api.patch('auth/me', {
        first_name: s.name.trim() || user?.first_name || 'Гость',
        last_name: user?.last_name || '',
      });
      await loadUser();
    } catch {
      // name is optional for flow
    }
  }

  async function handleCreate() {
    setBusy(true);
    setError(null);
    try {
      await saveName();
      await createWedding({
        name: s.weddingName.trim() || 'Наша свадьба',
        partner_name: s.partnerName.trim() || undefined,
        wedding_date: s.weddingDate || undefined,
        budget: s.budget ? Number(s.budget) : undefined,
        currency: s.currency || 'RUB',
      } as Partial<import('../../types').Wedding>);
      await loadWeddings().catch(() => {});
      patch({ step: 5 });
    } catch {
      setError('Не удалось создать свадьбу. Попробуйте ещё раз.');
    } finally {
      setBusy(false);
    }
  }

  async function handleJoin() {
    setBusy(true);
    setError(null);
    try {
      await saveName();
      await joinWedding(s.joinCode.trim());
      await loadWeddings().catch(() => {});
      patch({ step: 5 });
    } catch {
      setError('Не удалось присоединиться. Проверьте код.');
    } finally {
      setBusy(false);
    }
  }

  const step = s.step;

  return (
    <Panel>
      <Div style={{ padding: 16, maxWidth: 480, margin: '0 auto' }}>
        <Card style={{ padding: 18 }}>
          {/* Progress */}
          <Text style={{ fontSize: 12, color: 'var(--vkui--color_text_secondary)', marginBottom: 8 }}>
            Шаг {Math.min(step + 1, 6)} из 6
          </Text>

          {/* 1. Welcome + name */}
          {step === 0 && (
            <>
              <Headline level="1">Привет! 👋</Headline>
              <Text style={{ marginTop: 10, marginBottom: 16 }}>
                Я помогу настроить Colibri — планировщик свадьбы. Это займёт пару минут.
                Можно выйти в любой момент и вернуться позже.
              </Text>
              <FormItem top="Как вас зовут?">
                <Input
                  value={s.name}
                  onChange={(e) => patch({ name: e.target.value })}
                  placeholder="Имя"
                />
              </FormItem>
              <Button
                size="l"
                stretched
                mode="primary"
                disabled={!s.name.trim()}
                onClick={() => patch({ step: 1 })}
              >
                Продолжить
              </Button>
            </>
          )}

          {/* 2. Create or join */}
          {step === 1 && (
            <>
              <Headline level="1">Что делаем?</Headline>
              <Text style={{ marginTop: 10, marginBottom: 16 }}>
                Создайте свадьбу или присоединитесь к уже существующей по коду.
              </Text>
              <Button
                size="l"
                stretched
                mode="primary"
                style={{ marginBottom: 8 }}
                onClick={() => patch({ choice: 'create', step: 2 })}
              >
                Создать свадьбу
              </Button>
              <Button
                size="l"
                stretched
                mode="secondary"
                onClick={() => patch({ choice: 'join', step: 2 })}
              >
                Присоединиться по коду
              </Button>
              <Button size="m" mode="tertiary" style={{ marginTop: 12 }} onClick={() => patch({ step: 0 })}>
                Назад
              </Button>
            </>
          )}

          {/* 3a. Join code */}
          {step === 2 && s.choice === 'join' && (
            <>
              <Headline level="1">Код приглашения</Headline>
              <Text style={{ marginTop: 10, marginBottom: 16 }}>
                Введите код от владельца свадьбы (8 символов).
              </Text>
              <FormItem top="Код">
                <Input
                  value={s.joinCode}
                  onChange={(e) => patch({ joinCode: e.target.value.toUpperCase() })}
                  placeholder="Например: 0N2K6HNU"
                  autoCapitalize="characters"
                />
              </FormItem>
              <Button
                size="m"
                mode="secondary"
                style={{ marginBottom: 8 }}
                onClick={async () => {
                  const code = await readInviteCodeFromClipboard();
                  if (code) patch({ joinCode: code });
                  else setError('Не удалось прочитать буфер — вставьте код вручную');
                }}
              >
                Вставить из буфера
              </Button>
              {error && <Text style={{ color: 'var(--vkui--color_text_negative)', marginBottom: 8 }}>{error}</Text>}
              <Button
                size="l"
                stretched
                mode="primary"
                disabled={!s.joinCode.trim() || busy}
                onClick={handleJoin}
              >
                {busy ? 'Отправляем…' : 'Присоединиться'}
              </Button>
              <Button size="m" mode="tertiary" style={{ marginTop: 12 }} onClick={() => patch({ step: 1 })}>
                Назад
              </Button>
            </>
          )}

          {/* 3b. Wedding name + date */}
          {step === 2 && s.choice === 'create' && (
            <>
              <Headline level="1">Свадьба</Headline>
              <Text style={{ marginTop: 10, marginBottom: 16 }}>
                Название и дату можно изменить позже. Дату можно пропустить.
              </Text>
              <FormItem top="Название">
                <Input
                  value={s.weddingName}
                  onChange={(e) => patch({ weddingName: e.target.value })}
                  placeholder="Например: Свадьба Ани и Даши"
                />
              </FormItem>
              <FormItem top="Дата (необязательно)">
                <Input
                  type="date"
                  value={s.weddingDate}
                  onChange={(e) => patch({ weddingDate: e.target.value })}
                />
              </FormItem>
              <Button
                size="l"
                stretched
                mode="primary"
                style={{ marginBottom: 8 }}
                onClick={() => patch({ step: 3 })}
              >
                Далее
              </Button>
              <Button size="l" stretched mode="secondary" onClick={() => patch({ step: 3 })}>
                Пропустить
              </Button>
              <Button size="m" mode="tertiary" style={{ marginTop: 12 }} onClick={() => patch({ step: 1 })}>
                Назад
              </Button>
            </>
          )}

          {/* 4. Budget */}
          {step === 3 && s.choice === 'create' && (
            <>
              <Headline level="1">Бюджет</Headline>
              <Text style={{ marginTop: 10, marginBottom: 16 }}>
                Примерный бюджет свадьбы. Можно пропустить и заполнить позже.
              </Text>
              <FormItem top="Сумма">
                <Input
                  type="number"
                  value={s.budget}
                  onChange={(e) => patch({ budget: e.target.value })}
                  placeholder="0"
                />
              </FormItem>
              <FormItem top="Валюта">
                <Select
                  value={s.currency}
                  onChange={(e) => patch({ currency: e.currentTarget.value })}
                  options={[
                    { label: 'Рубль (RUB)', value: 'RUB' },
                    { label: 'Доллар (USD)', value: 'USD' },
                    { label: 'Евро (EUR)', value: 'EUR' },
                  ]}
                />
              </FormItem>
              {error && <Text style={{ color: 'var(--vkui--color_text_negative)', marginBottom: 8 }}>{error}</Text>}
              <Button
                size="l"
                stretched
                mode="primary"
                style={{ marginBottom: 8 }}
                onClick={() => patch({ step: 4 })}
              >
                Далее
              </Button>
              <Button size="l" stretched mode="secondary" onClick={() => patch({ step: 4 })}>
                Пропустить
              </Button>
              <Button size="m" mode="tertiary" style={{ marginTop: 12 }} onClick={() => patch({ step: 2 })}>
                Назад
              </Button>
            </>
          )}

          {/* 5. Partner name, then create */}
          {step === 4 && s.choice === 'create' && (
            <>
              <Headline level="1">Партнёр</Headline>
              <Text style={{ marginTop: 10, marginBottom: 16 }}>
                Укажите имя партнёра — после создания свадьбы можно сразу отправить ему приглашение.
              </Text>
              <FormItem top="Имя партнёра (необязательно)">
                <Input
                  value={s.partnerName}
                  onChange={(e) => patch({ partnerName: e.target.value })}
                  placeholder="Имя"
                />
              </FormItem>
              {error && <Text style={{ color: 'var(--vkui--color_text_negative)', marginBottom: 8 }}>{error}</Text>}
              <Button
                size="l"
                stretched
                mode="primary"
                style={{ marginBottom: 8 }}
                disabled={busy}
                onClick={handleCreate}
              >
                {busy ? 'Создаём…' : 'Создать свадьбу'}
              </Button>
              <Button size="l" stretched mode="secondary" disabled={busy} onClick={handleCreate}>
                Пропустить имя
              </Button>
              <Button size="m" mode="tertiary" style={{ marginTop: 12 }} onClick={() => patch({ step: 3 })}>
                Назад
              </Button>
            </>
          )}

          {/* 6. Done + partner invite */}
          {step === 5 && (
            <>
              <Headline level="1">
                {s.choice === 'join' ? 'Вы присоединились!' : 'Готово!'}
              </Headline>
              {s.choice === 'join' ? (
                <Text style={{ marginTop: 10, marginBottom: 16 }}>
                  Свадьба уже доступна — она появилась на главной.
                </Text>
              ) : (
                <>
                  <Text style={{ marginTop: 10, marginBottom: 12 }}>
                    Свадьба создана. Отправьте партнёру код или ссылку — он сможет присоединиться.
                  </Text>
                  {currentWedding && (
                    <Card style={{ padding: 12, marginBottom: 12, background: 'var(--vkui--color_background_content)' }}>
                      <Text style={{ fontSize: 13, color: 'var(--vkui--color_text_secondary)' }}>
                        Код: <strong style={{ color: 'var(--vkui--color_text)' }}>{currentWedding.invite_code}</strong>
                      </Text>
                      <Div style={{ padding: '12px 0 0' }}>
                        <ShareInviteButton
                          size="m"
                          mode="primary"
                          stretched
                          invite={{
                            code: currentWedding.invite_code,
                            link: buildInviteLink(currentWedding.invite_code),
                            title: currentWedding.name,
                          }}
                        />
                      </Div>
                    </Card>
                  )}
                </>
              )}
              <Button size="l" stretched mode={s.choice === 'join' ? 'primary' : 'secondary'} onClick={finish}>
                Открыть Colibri
              </Button>
            </>
          )}

          {busy && step !== 5 && (
            <Div style={{ padding: '12px 0 0', display: 'flex', justifyContent: 'center' }}>
              <Spinner size="m" />
            </Div>
          )}
        </Card>

        {/* Skip link */}
        {step < 5 && (
          <Div style={{ textAlign: 'center', paddingTop: 16 }}>
            <Button mode="tertiary" size="m" onClick={finish}>
              Пропустить онбординг
            </Button>
          </Div>
        )}
      </Div>
    </Panel>
  );
}
